<template>
  <el-config-provider :locale="currentLocale">
    <router-view />
  </el-config-provider>
</template>

<script setup lang="ts">
import { computed, watch, onMounted, ref, onUnmounted } from "vue";
import { LanguageManager } from "@/utils/language";
import { ElConfigProvider, ElMessageBox, ElNotification } from "element-plus";
import type { NotificationHandle } from "element-plus";
import { AutoUpdater } from "@/utils/updater";

// 生产环境
const isProduction = import.meta.env.PROD;

// 使用 LanguageManager 获取当前语言包
const currentLocale = computed(() => LanguageManager.getElementPlusLocale());

// 防止重复触发的标志位
const hasNotified = ref(false);

// 更新器
let updater: AutoUpdater | null = null;

onMounted(() => {
  if (!isProduction) return;

  updater = new AutoUpdater();

  updater.onUpdate(() => {
    if (hasNotified.value) return;
    hasNotified.value = true;

    // 触发更新流程
    promptUserToUpdate();
  });
});

onUnmounted(() => {
  if (updater) {
    updater.destroy();
  }
});

/**
 * 先弹窗，拒绝则转为右下角常驻
 */
function promptUserToUpdate() {
  ElMessageBox.confirm(
    "系統偵測到新版本，為確保功能正常，請立即刷新頁面。",
    "版本更新提示",
    {
      confirmButtonText: "立即刷新",
      cancelButtonText: "稍後", // 用户点击此按钮
      type: "warning",
      closeOnClickModal: false,
      closeOnPressEscape: false,
      distinguishCancelAndClose: true,
    },
  )
    .then(() => {
      // 点击"立即刷新"
      window.location.reload();
    })
    .catch(() => {
      // 点击"稍后"或关闭弹窗 -> 进入常驻模式
      showPersistentNotification();
    });
}

/**
 * 常驻通知（无法关闭，直到刷新）
 */
function showPersistentNotification() {
  ElNotification({
    title: "更新就緒",
    message: "新版本已準備好，點擊此處重繪。",
    type: "primary",
    position: "bottom-right",
    duration: 0, // 关键：0 代表永远不自动关闭
    showClose: false, // 关键：隐藏关闭按钮，强制用户只能点刷新
    offset: 50,
    onClick: () => {
      window.location.reload();
    },
  });
}

// 监听语言变化
watch(
  () => LanguageManager.getCurrentLanguage(),
  () => {
    console.log(
      "Language changed, Element Plus locale updated to:",
      currentLocale.value,
    );
  },
);
</script>

<style>
@import './assets/fonts/fonts.css';
</style>